import {
    useRef,
    useState
} from 'react';

import {Alert, SimpleGrid} from '@mantine/core';
import {I18n} from '@aws-amplify/core';
import {useVerifiedPermissions} from '@flexinet/auth-components';

import {meterAssignmentsQueryKeys, useMeterAssignments} from '../hooks/useMeterAssignments';
import {useMeterAssignmentsSelection} from '../hooks/useMeterAssignmentsSelection';
import {useMeterAssignmentsTableColumns} from '../hooks/useMeterAssignmentsTableColumns';
import {useCustomerLabels} from '../hooks/useCustomerLabels';
import {FormDrawer} from '../drawers/FormDrawer';
import {update} from '../services/MeterAssignmentsService';
import {buildUpdateMeterAssignmentInput} from '../services/buildMeterAssignmentInput';
import {rbacActions} from '../../../config/rbac/actions';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';
import {MeterAssignment, MeterAssignmentRow} from '../types';
import {useQueryClient} from '@tanstack/react-query';

export const RecordsTable = () => {
    const {selectedRecords, setSelectedRecords} = useMeterAssignmentsSelection();
    const {columns} = useMeterAssignmentsTableColumns();
    const customerLabel = useCustomerLabels();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<MeterAssignment | null>(null);
    const queryClient = useQueryClient();

    // Editing is a write, so the row click that opens the edit drawer is gated on
    // UpdateMeterAssignment, as Add and Delete are gated on theirs (IndexPageHeader).
    const {allowedActions} = useVerifiedPermissions();
    const canEdit = allowedActions.includes(rbacActions.UpdateMeterAssignment);

    const {
        items,
        visibleItems,
        keyword,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingMore,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = useMeterAssignments({customerLabel});

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const term = keyword.trim();

    // Resolves true when saved; the drawer closes only then (FormDrawer), so a server
    // rejection (an unlinked customer, a disabled channel) keeps the operator's values.
    // The input is the buildUpdateMeterAssignmentInput whitelist, unchanged.
    const handleDrawerSubmit = async (values: MeterAssignment): Promise<boolean> => {
        if (!selectedRecord?.id) {
            return false;
        }
        try {
            await update(selectedRecord.id, buildUpdateMeterAssignmentInput(values) as MeterAssignment);
            queryClient.invalidateQueries({queryKey: meterAssignmentsQueryKeys.all});
            notifySuccess(I18n.get('Meter Assignment updated'));
            return true;
        } catch (e) {
            notifyError(e);
            return false;
        }
    };

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title={I18n.get('Could not load meter assignments')}>
                    {I18n.get('The billing API did not return the meter assignments list. Try Reload; if it persists, the API may be unavailable in this environment.')}
                </Alert>
            )}

            <MantineDataTable<MeterAssignmentRow>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far, the same scope as
                // the keyword filter. Token pagination cannot reorder unseen rows.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                selectedRecords={selectedRecords as MeterAssignmentRow[]}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={canEdit ? ({record}) => {
                    setSelectedRecord(record);
                    setDrawerOpened(true);
                } : undefined}
            />

            {/*
              TWO EMPTY STATES (§3, §13). "No meter assignments yet" invites the first
              action; "nothing matches" means the search is too narrow. Gated on !loading so
              it does not flash during the first fetch, and on !isError so a failed load
              keeps its own Alert rather than claiming there are none.
            */}
            {!loading && !isError && visibleItems.length === 0 && (
                <Alert
                    color={term ? 'gray' : 'blue'}
                    title={term ? I18n.get('No matching meter assignments') : I18n.get('No meter assignments yet')}
                >
                    {term
                        ? `${I18n.get('Nothing in the loaded meter assignments matches')} "${term}". ${I18n.get('Clear the search to see every loaded row. A match may still be on a page that has not been loaded yet.')}`
                        : I18n.get('A meter assignment subscribes a customer to a meter on a billing channel. Use Add Meter Assignment to create the first one.')}
                </Alert>
            )}

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'meter assignment'}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchRecords}
            />

            {drawerOpened && selectedRecord && (
                <FormDrawer
                    record={selectedRecord}
                    handleSubmit={handleDrawerSubmit}
                    opened={drawerOpened}
                    onClose={() => {
                        setDrawerOpened(false);
                        setSelectedRecord(null);
                    }}
                />
            )}
        </SimpleGrid>
    );
};
